import { SagaIterator } from 'redux-saga';
import { cancel, put, retry, select } from 'redux-saga/effects';
import {
	getGroupAccessId,
	getPrimaryGradepoint,
	getSubjectsTakenByPrimaryGradepoint,
	hasGroupAccess,
	setSubjectsTaken,
	setSubjectsTakenError,
} from '../context';
import { Context } from 'features/app/types';
import { sagaFlowConfigurations } from '../../../../constants';
import { getSubjectsByPrimaryGradepointId, getSubjectsByTrendId } from '../../../../api/contextAPI';
import { getFeature } from '../features';
import { getGroupSelectedTrendId } from 'features/groups/analysis/redux';
import { getSubjectsByIdApi } from '../../../../api/bedrockAPI';

/**
 * Saga that fetches a list of subjects taken for a given primary gradepoint id
 */
export function* fetchSubjectsTakenFlow(): SagaIterator<void> {
	//Get the Primary Grade Point
	const primaryGradePoint: Context.Gradepoint = yield select(getPrimaryGradepoint);

	//check if we already have the subjects for this gradepoint
	const subjectsTaken = yield select(getSubjectsTakenByPrimaryGradepoint);

	//get the feature flags
	const featureFlag = yield select(getFeature('ui_subjects_taken'));

	//is the school a mat
	const isMat = yield select(hasGroupAccess);

	//Gets the active trend
	const activeTrend = yield select(getGroupSelectedTrendId);

	//const get selected examlevel from summit calls
	const groupAccessId = yield select(getGroupAccessId);

	//check to see if we already have the subjects for this gradepoint
	if (!isMat && subjectsTaken && subjectsTaken.gradepointId === primaryGradePoint.gradepointId) {
		//if we do, cancel the saga
		yield cancel();
	}

	let response: ResponseBuilder<Context.SubjectsTaken[]>;
	//If this is a MAT logging n and the Id is defined call that endpoint
	try {
		if (isMat) {
			if (!activeTrend || groupAccessId === 0) {
				yield cancel();
			}

			//trend ID
			response = yield retry(
				sagaFlowConfigurations.apiRetry, // Retry
				sagaFlowConfigurations.milliSeconds, // Delay between retry (MS)
				getSubjectsByTrendId,
				activeTrend,
				groupAccessId
			);
		} else {
			//Single school sign in
			response = yield retry(
				sagaFlowConfigurations.apiRetry, // Retry
				sagaFlowConfigurations.milliSeconds, // Delay between retry (MS)
				getSubjectsByPrimaryGradepointId,
				primaryGradePoint.gradepointId
			);
		}
		//if there is an error, set the error in redux and cancel the saga
		if (response?.hasError) {
			//set the error in redux
			yield put(setSubjectsTakenError(response.errors[0].message));
			//cancel the saga
			yield cancel();
		}

		//get the subject ids
		const subjectIds = response.responseObject.map((subject) => subject.subjectId);

		//get the subjects from bedrock
		const bedrockResponse = yield retry(
			sagaFlowConfigurations.apiRetry, // Retry
			sagaFlowConfigurations.milliSeconds, // Delay between retry (MS)
			getSubjectsByIdApi,
			subjectIds
		);

		//if there is an error, set the error in redux and cancel the saga
		if (bedrockResponse?.hasError) {
			//set the error in redux
			yield put(setSubjectsTakenError(bedrockResponse.errors[0].message));
			//cancel the saga
			yield cancel();
		}

		const subjectsWithBedrock: Context.SubjectsTaken[] = response.responseObject.map((subject) => {
			const bedrockSubject = bedrockResponse.responseObject.find(
				(bedrockSubject) => bedrockSubject.alpsSubjectId === subject.subjectId
			);
			return {
				subjectId: subject.subjectId,
				subjectName: bedrockSubject?.display,
			};
		});

		//format the response into the correct type to match the subjects to the gp id
		const subjectsByPrimaryGradepoint: Context.SubjectsByPrimaryGradepoint = {
			gradepointId: primaryGradePoint.gradepointId,
			subjects: subjectsWithBedrock,
		};

		//set the subjects taken in redux
		yield put(setSubjectsTaken(subjectsByPrimaryGradepoint));
		//bail
		yield cancel();
	} catch (err) {
		//set the error in redux
		yield put(setSubjectsTakenError('Error fetching subjects taken'));
		//cancel the saga
		yield cancel();
	}
}
